// @ts-strict-ignore
import { FC } from 'react';

import { Dialogs } from 'analytics/events/dialog';

import { observer } from 'mobx-react';

import { useStores } from 'mobx/hooks/useStores';

import { StatusUpdateBody } from 'fetchers/CareManagementFetcher';
import { CmStatusEnum } from 'fetchers/responses/care-management.response';

import { CM_INELIGIBILITY, CM_OPT_OUT, EPISODE_END } from 'constants/reasons.const';

import { CmPatient } from 'models/CmPatient';

import CareManagementSelectReasonModal from 'views/Pages/CareManagement/CareManagementReasonModal/CareManagementSelectReasonModal';

import { REASON_TYPE_CM, WarningTypes } from 'views/Pages/CareManagement/CareManagementReport';

import { MessageDialog } from 'components/UIkit/atoms/Dialog';

interface Props {
  patient: CmPatient;
  setActivePatient: (cmPatient: CmPatient) => void;
  warningType: WarningTypes | null;
  setWarningType: (warningType: WarningTypes | null) => void;
  setReasonType: (reasonType: REASON_TYPE_CM | null) => void;
  reasonType: REASON_TYPE_CM | null;
  eligibilityStatus: CmStatusEnum.EligibleForCcm | CmStatusEnum.EligibleForPcm;
  setEligibilityStatus: (
    eligibilityStatus: CmStatusEnum.EligibleForCcm | CmStatusEnum.EligibleForPcm
  ) => void;
  markPatientEligible: (
    cmPatient: CmPatient,
    status: CmStatusEnum.EligibleForCcm | CmStatusEnum.EligibleForPcm
  ) => Promise<void>;
  enrollPatientInEpisode: (cmPatient: CmPatient) => Promise<void>;
  getPatientId: (cmPatient: CmPatient) => Promise<number>;
  episodeId: number;
  setEpisodeId: (episodeId: number) => void;
  onUpdateStatus: () => void;
  isCmSelectReasonModalOpen: boolean;
  toggleCmSelectReasonModalOpen: () => void;
}

const reasonTypeToStatusMap = {
  [CM_INELIGIBILITY]: CmStatusEnum.Ineligible,
  [CM_OPT_OUT]: CmStatusEnum.OptedOut,
  [EPISODE_END]: CmStatusEnum.Ended
};

export const CareManagementModals: FC<Props> = ({
  patient,
  setActivePatient,
  warningType,
  setWarningType,
  reasonType,
  setReasonType,
  eligibilityStatus,
  setEligibilityStatus,
  markPatientEligible,
  enrollPatientInEpisode,
  getPatientId,
  episodeId,
  setEpisodeId,
  onUpdateStatus,
  isCmSelectReasonModalOpen,
  toggleCmSelectReasonModalOpen
}) => {
  const { careManagementReportStore } = useStores();

  const resetWarning = () => setWarningType(null);

  const handleStatusChanged = async (status: StatusUpdateBody) => {
    const patientId = await getPatientId(patient);
    await careManagementReportStore.updateStatus(patientId, status, onUpdateStatus);
    setActivePatient(null);
    setReasonType(null);
  };

  return (
    <>
      <CareManagementSelectReasonModal
        reasonType={reasonType}
        isOpen={isCmSelectReasonModalOpen}
        onClose={toggleCmSelectReasonModalOpen}
        resetDataAfterClose={() => setReasonType(null)}
        setSelectedReason={(reasonId, note) =>
          handleStatusChanged({
            status: reasonTypeToStatusMap[reasonType],
            reasonId,
            notes: note
          })
        }
      />
      <MessageDialog
        id={Dialogs.HasActiveCmEpisode}
        isOpen={warningType === WarningTypes.OptOutWhenActive}
        title="Patient has Active Care Management Episode"
        handleClose={resetWarning}
        primaryActionProps={{
          text: 'End & Opt out',
          onClick: () => {
            resetWarning();
            setReasonType(CM_OPT_OUT);
            toggleCmSelectReasonModalOpen();
          }
        }}
        secondaryActionProps={{ text: 'Cancel', onClick: resetWarning }}
      >
        If you proceed to opt them out of Care Management, this active episode will be ended
        immediately.
      </MessageDialog>

      <MessageDialog
        id={Dialogs.HasOptOutOfCm}
        isOpen={warningType === WarningTypes.RemoveEnrollWhenOptedOut}
        title="Patient has Opted Out of Care Management"
        handleClose={resetWarning}
        primaryActionProps={{
          text: 'Opt In',
          onClick: () => {
            resetWarning();
            handleStatusChanged({ status: CmStatusEnum.None });
          }
        }}
        secondaryActionProps={{ text: 'Cancel', onClick: resetWarning }}
      >
        <div className="d-flex flex-column">
          <span className="mb-3">
            This patient has opted out of Care Management with the following reason: "
            {patient?.cmStatus.reasonText}"
          </span>
          <span>Would you like to opt them back in?</span>
        </div>
      </MessageDialog>

      <MessageDialog
        id={Dialogs.HasOptOutOfCm}
        isOpen={warningType === WarningTypes.MarkIneligibleWhenOptedOut}
        title="Patient has Opted Out of Care Management"
        handleClose={resetWarning}
        primaryActionProps={{
          text: 'Opt In, Mark Ineligible',
          onClick: () => {
            resetWarning();
            setReasonType(CM_INELIGIBILITY);
            toggleCmSelectReasonModalOpen();
          }
        }}
        secondaryActionProps={{ text: 'Cancel', onClick: resetWarning }}
      >
        <div className="d-flex flex-column">
          <span className="mb-3">
            This patient has opted out of Care Management with the following reason: "
            {patient?.cmStatus.reasonText}"
          </span>
          <span>Would you like to opt them back in and mark them as ineligible?</span>
        </div>
      </MessageDialog>

      <MessageDialog
        id={Dialogs.MarkedAsIneligibleForCm}
        isOpen={
          warningType === WarningTypes.MarkEligibleOrEnrollWhenIneligible ||
          warningType === WarningTypes.RemoveEnrollWhenIneligible
        }
        title="Patient is Marked as Ineligible for Care Management"
        handleClose={() => {
          resetWarning();
          if (eligibilityStatus) {
            setEligibilityStatus(null);
          }
          if (episodeId) {
            setEpisodeId(null);
          }
        }}
        primaryActionProps={{
          text: 'Continue',
          onClick: () => {
            if (warningType === WarningTypes.RemoveEnrollWhenIneligible) {
              resetWarning();
              handleStatusChanged({ status: CmStatusEnum.None });
              return;
            }
            resetWarning();
            if (eligibilityStatus) {
              markPatientEligible(patient, eligibilityStatus);
            }
            if (episodeId) {
              enrollPatientInEpisode(patient);
            }
          }
        }}
        secondaryActionProps={{
          text: 'Cancel',
          onClick: () => {
            resetWarning();
            if (eligibilityStatus) {
              setEligibilityStatus(null);
            }
            if (episodeId) {
              setEpisodeId(null);
            }
          }
        }}
      >
        {' '}
        <div className="d-flex flex-column">
          <span className="mb-3">
            This patient has been marked ineligible for Care Management for the following reason: "
            {patient?.cmStatus.reasonText}"
          </span>
          <span>Do you wish to undo this and continue?</span>
        </div>
      </MessageDialog>

      <MessageDialog
        id={Dialogs.CmEpisodeEnded}
        isOpen={warningType === WarningTypes.EnrollWhenEnded}
        title="Patient Was in a Care Management Episode That Was Ended"
        handleClose={() => {
          resetWarning();
          setEpisodeId(null);
        }}
        primaryActionProps={{
          text: 'Enroll',
          onClick: () => {
            resetWarning();
            enrollPatientInEpisode(patient);
          }
        }}
        secondaryActionProps={{
          text: 'Cancel',
          onClick: () => {
            resetWarning();
            setEpisodeId(null);
          }
        }}
      >
        <div className="d-flex flex-column">
          <span className="mb-3">
            This patient was in a Care Management episode that was ended for the following reason: "
            {patient?.cmStatus.reasonText}"
          </span>
          <span>Do you wish to enroll them in a new one, anyway?</span>
        </div>
      </MessageDialog>

      <MessageDialog
        id={Dialogs.HasOptOutOfCm}
        isOpen={warningType === WarningTypes.EnrollWhenOptedOut}
        title="Patient Opted Out of Care Management"
        handleClose={() => {
          resetWarning();
          setEpisodeId(null);
        }}
        primaryActionProps={{
          text: 'Opt In & Enroll',
          onClick: () => {
            resetWarning();
            enrollPatientInEpisode(patient);
          }
        }}
        secondaryActionProps={{
          text: 'Cancel',
          onClick: () => {
            resetWarning();
            setEpisodeId(null);
          }
        }}
      >
        <div className="d-flex flex-column">
          <span className="mb-3">
            This patient has opted out of Care Management with the following reason: "
            {patient?.cmStatus.reasonText}"
          </span>
          <span>Would you like to opt them back in to enroll them in this episode?</span>
        </div>
      </MessageDialog>
    </>
  );
};

export default observer(CareManagementModals);
